import React from "react";
import { Row, Col, Spin, Alert, Card, Typography, Layout } from "antd";
import { theme } from "./constants/theme";
import StatisticsCards from "./components/StatisticsCards";
import SummaryTables from "./components/SummaryTables";
import DualVehicleMaps from "./components/DualVehicleMaps";
import { useDashboardData } from "./hooks/useDashboardData";
import { getCantonPercentage } from "./utils/formatters";
import * as XLSX from "xlsx";
import logoAyasa from "../../assets/ayasa.png";
import logoRenault from "../../assets/0emwie0m.png";

const { Title } = Typography;
const { Content } = Layout;

// Estilos para los logos y el contenedor
const logoContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "15px",
};

const logoStyle = {
  height: "32px",
  objectFit: "contain",
};

const renaultLogoStyle = {
  height: "40px",
  objectFit: "contain",
};

const titleContainerStyle = {
  display: "flex",
  alignItems: "center",
  flex: 1,
  justifyContent: "center",
};

const VehicleDistributionDashboard = () => {
  const {
    provinceStats,
    cantonStats,
    nightCantonStats,
    globalStats,
    parishStats,
    parishDealerStats,
    agencies,
    loading,
    error,
  } = useDashboardData();

  // Detectar si estamos en un dispositivo móvil
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  // Efecto para detectar cambios en el tamaño de la pantalla
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "50px",
          background: "#fff",
          borderRadius: "8px",
          margin: "24px",
        }}
      >
        <Spin size="large" />
        <p style={{ marginTop: "16px", color: "#1890ff" }}>
          Cargando datos del dashboard...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error en la carga de datos"
        description={error}
        type="error"
        showIcon
        style={{ margin: "24px" }}
      />
    );
  }

  const getTotalVehicles = () => {
    return provinceStats.reduce(
      (sum, stat) => sum + parseInt(stat.total_vehicles || 0),
      0
    );
  };

  const getProvincePercentage = (province) => {
    const total = getTotalVehicles();
    if (total === 0) return 0;
    const provinceStat = provinceStats.find(
      (stat) => stat.day_province === province
    );
    if (!provinceStat) return 0;
    return ((parseInt(provinceStat.total_vehicles) / total) * 100).toFixed(0);
  };

  const downloadExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");

    const maxWidth = Object.keys(data[0]).reduce((acc, key) => {
      const maxLength = Math.max(
        key.length,
        ...data.map((item) => String(item[key]).length)
      );
      acc[key] = maxLength + 2;
      return acc;
    }, {});

    worksheet["!cols"] = Object.values(maxWidth).map((width) => ({ width }));
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const handleDownloadCantones = () => {
    const data = [
      {
        Provincia: "PICHINCHA",
        Canton: "",
        "Cantidad Vehículos": pichinchaTotal,
        Porcentaje: `${getProvincePercentage("Pichincha")}%`,
      },
      ...cantonStats.Pichincha.map((canton) => ({
        Provincia: "PICHINCHA",
        Canton: canton.day_canton,
        "Cantidad Vehículos": canton.total_vehicles,
        Porcentaje: `${getCantonPercentage(canton, pichinchaTotal)}%`,
      })),
      {
        Provincia: "GUAYAS",
        Canton: "",
        "Cantidad Vehículos": guayasTotal,
        Porcentaje: `${getProvincePercentage("Guayas")}%`,
      },
      ...cantonStats.Guayas.map((canton) => ({
        Provincia: "GUAYAS",
        Canton: canton.day_canton,
        "Cantidad Vehículos": canton.total_vehicles,
        Porcentaje: `${getCantonPercentage(canton, guayasTotal)}%`,
      })),
    ];

    downloadExcel(data, "Resumen_Vehiculos_por_Canton");
  };

  const handleDownloadParishDealers = (
    province,
    timeOfDay,
    selectedDealer = null
  ) => {
    const data = [];
    Object.entries(parishDealerStats[timeOfDay])
      .filter(([parish]) => parish.startsWith(province))
      .forEach(([parish, stats]) => {
        // Aplicar filtro de concesionario si está seleccionado
        let dealersToExport = stats.dealers;
        if (selectedDealer) {
          dealersToExport = stats.dealers.filter(
            (dealer) => dealer.name === selectedDealer
          );

          // Si no hay concesionarios que coincidan con el filtro, omitir esta parroquia
          if (dealersToExport.length === 0) return;
        }

        dealersToExport.forEach((dealer) => {
          data.push({
            Parroquia: parish,
            "Total Vehículos Parroquia": stats.total_vehicles,
            Concesionario: dealer.name,
            "Vehículos Concesionario": dealer.vehicles,
            Porcentaje: `${dealer.percentage}%`,
            "Distancia Promedio (km)":
              dealer.avgDistance !== undefined ? dealer.avgDistance : "N/A",
          });
        });
      });

    let filename = `Resumen_Concesionarios_Parroquias_${province}_${timeOfDay}`;
    if (selectedDealer) {
      filename += `_${selectedDealer.replace(/\s+/g, "_")}`;
    }

    downloadExcel(data, filename);
  };

  const totalVehicles = getTotalVehicles();
  const pichinchaTotal =
    provinceStats.find((stat) => stat.day_province === "Pichincha")
      ?.total_vehicles || 0;
  const guayasTotal =
    provinceStats.find((stat) => stat.day_province === "Guayas")
      ?.total_vehicles || 0;

  return (
    <Layout style={{ minHeight: "100vh", background: "#f7f9fc" }}>
      <Content
        style={{ padding: isMobile ? "12px" : "24px", background: "#f7f9fc" }}
      >
        <Card
          style={{ ...theme.cards.base, padding: 0 }}
          bodyStyle={{ padding: 0 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: isMobile ? "8px 16px" : "10px 24px",
              height: isMobile ? "56px" : "64px",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            <div style={logoContainerStyle}>
              <img src={logoAyasa} alt="AYASA Logo" style={logoStyle} />
              <img
                src={logoRenault}
                alt="Renault Logo"
                style={renaultLogoStyle}
              />
            </div>
            <div style={titleContainerStyle}>
              <Title
                level={4}
                style={{
                  margin: 0,
                  color: theme.colors.text,
                  fontSize: isMobile ? "14px" : "18px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {isMobile
                  ? "Distribución de Vehículos"
                  : "CONCENTRACIÓN DE PARQUE VEHICULAR POR PROVINCIAS"}
              </Title>
            </div>
          </div>
        </Card>

        <div style={{ marginTop: "24px" }}>
          <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
            <StatisticsCards
              totalVehicles={totalVehicles}
              pichinchaTotal={pichinchaTotal}
              guayasTotal={guayasTotal}
              globalStats={globalStats}
              getProvincePercentage={getProvincePercentage}
            />
          </Row>

          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card
                style={{
                  ...theme.cards.base,
                  padding: 0,
                  marginBottom: "24px",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: isMobile ? "8px 16px" : "10px 24px",
                    height: isMobile ? "56px" : "64px",
                  }}
                >
                  <Title
                    level={4}
                    style={{
                      margin: 0,
                      color: theme.colors.text,
                      fontSize: isMobile ? "14px" : "18px",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    RESUMEN DETALLADO DE PARQUE VEHICULAR POR CANTÓN Y PARROQUIA
                  </Title>
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            <SummaryTables
              cantonStats={cantonStats}
              parishStats={parishStats}
              parishDealerStats={parishDealerStats}
              pichinchaTotal={pichinchaTotal}
              guayasTotal={guayasTotal}
              handleDownloadCantones={handleDownloadCantones}
              handleDownloadParishDealers={handleDownloadParishDealers}
            />
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
            <Col span={24}>
              <Card
                style={{
                  ...theme.cards.base,
                  padding: 0,
                  overflow: "hidden",
                  minHeight: "700px",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <DualVehicleMaps
                  dayData={cantonStats}
                  nightData={nightCantonStats}
                  agencies={agencies}
                  loading={loading}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </Content>
    </Layout>
  );
};

export default VehicleDistributionDashboard;
